import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Calendar } from "../../../Icon/icons/calendar"
import { Home } from "../../../Icon/icons/home"
import { Map } from "../../../Icon/icons/map"
import { homeColor, homeSize, homeText } from "../home-theme.ts"
import { ChartNoAxesCombinedIcon, TrendingUpIcon } from "./home-icons.tsx"
import {
  HOME_SECTION_SPECS,
  type HomeSectionId,
  sectionAnchorId
} from "./homeSectionModel.ts"

type Glyph = React.FC<React.SVGProps<SVGSVGElement>>

/**
 * The shared icon library, not a new set drawn for this bar. Locations takes
 * the same location pin the map's own Lead marker uses, so the nav and the
 * markers below it read as one vocabulary.
 */
const SECTION_GLYPH: Record<HomeSectionId, Glyph> = {
  today: Home,
  locations: Map,
  financial: ChartNoAxesCombinedIcon,
  schedule: Calendar,
  pipeline: TrendingUpIcon
}

/**
 * How far below the scrollport's top edge a heading counts as "the section
 * you are reading". The nav is sticky at the top of that box, so a heading
 * level with the viewport's top is actually UNDER the nav — the spy has to
 * discount its height or the previous section stays lit while you read the
 * next one.
 */
const NAV_HEIGHT = 48

/** The page's scroll box is `FixedContainer`'s own column, never the window. */
const scrollBoxOf = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null
  while (current) {
    const overflowY = getComputedStyle(current).overflowY
    if (overflowY === "auto" || overflowY === "scroll") return current
    current = current.parentElement
  }
  return null
}

/**
 * Which section is in view.
 *
 * Measured against the scroll box's own rect rather than through
 * `IntersectionObserver` thresholds: a section taller than the viewport never
 * crosses a ratio threshold at all, and two short ones cross together. The
 * rule here is simply "the last heading at or above the reading line", which
 * is what a reader would say, and it holds for a section of any height.
 */
export const useHomeSectionSpy = (
  sections: readonly HomeSectionId[]
): HomeSectionId | null => {
  const [active, setActive] = useState<HomeSectionId | null>(
    sections[0] ?? null
  )
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  useEffect(() => {
    const first = document.getElementById(
      sectionAnchorId(sections[0] ?? "today")
    )
    const box = scrollBoxOf(first)
    const target: HTMLElement | Window = box ?? window

    // Order-independent on purpose: the caller lists the sections it renders,
    // and the page's own order is whatever the layout happens to put on
    // screen. Picking the LOWEST heading still at or above the reading line
    // reads the DOM as it actually is, so a nav listed out of page order (or
    // a layout a user has reordered) still lights the right link.
    const read = () => {
      const top = box ? box.getBoundingClientRect().top : 0
      const line = top + NAV_HEIGHT + 1
      let current: HomeSectionId | null = null
      let best = -Infinity
      let highest: { id: HomeSectionId; top: number } | null = null
      for (const id of sectionsRef.current) {
        const el = document.getElementById(sectionAnchorId(id))
        if (!el) continue
        const elTop = el.getBoundingClientRect().top
        if (!highest || elTop < highest.top) highest = { id, top: elTop }
        if (elTop <= line && elTop > best) {
          best = elTop
          current = id
        }
      }
      // Above every heading (the page is scrolled to the very top), the first
      // one on the page is what you are about to read.
      setActive(current ?? highest?.id ?? null)
    }

    read()
    target.addEventListener("scroll", read, { passive: true })
    window.addEventListener("resize", read)
    return () => {
      target.removeEventListener("scroll", read)
      window.removeEventListener("resize", read)
    }
    // `sections` is read through a ref so a re-render with a new array
    // identity does not tear down and re-attach the listener every frame.
  }, [sections.length, sections[0]])

  return active
}

interface HomeSectionNavProps {
  /** Only the sections that actually have something to show, in page order. */
  sections: readonly HomeSectionId[]
}

/**
 * Section links that scroll the page, not routes.
 *
 * Real anchors, so the browser's own focus, middle-click and context menu all
 * work; the click is intercepted only to keep the router's URL untouched and
 * to land the heading clear of the sticky nav. `aria-current` carries the
 * active state, so it survives a palette nobody can see — §11's "statuses
 * that do not depend on colour alone".
 */
export const HomeSectionNav = ({ sections }: HomeSectionNavProps) => {
  const { t } = useTranslation()
  const active = useHomeSectionSpy(sections)

  const goTo = useCallback((id: HomeSectionId) => {
    const el = document.getElementById(sectionAnchorId(id))
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "start" })
    // Scrolling alone leaves the keyboard where it was, so the next Tab would
    // continue from the nav rather than from the section just opened.
    el.focus({ preventScroll: true })
  }, [])

  if (sections.length < 2) return null

  // Rendered in the order the caller gave, which is the order the page puts
  // the sections in — not the model's own canonical order, which a layout the
  // user has rearranged no longer matches.
  const specs = sections
    .map((id) => HOME_SECTION_SPECS.find((spec) => spec.id === id))
    .filter((spec): spec is (typeof HOME_SECTION_SPECS)[number] =>
      Boolean(spec)
    )

  return (
    <nav
      aria-label={t("home.sectionNav.label", "Home sections")}
      className={"home-section-nav"}
      style={{
        background: homeColor.surface,
        border: `1px solid ${homeColor.border}`,
        borderRadius: homeSize.cardRadius
      }}
    >
      <ul className={"home-section-nav__list"}>
        {specs.map((spec) => {
          const Glyph = SECTION_GLYPH[spec.id]
          const isActive = spec.id === active
          return (
            <li key={spec.id}>
              <a
                href={`#${sectionAnchorId(spec.id)}`}
                aria-current={isActive ? "true" : undefined}
                className={"home-section-nav__link"}
                data-active={isActive ? "true" : undefined}
                style={{
                  ...homeText.button,
                  color: isActive ? homeColor.teal : homeColor.inkMuted,
                  background: isActive ? homeColor.tealPale : "transparent"
                }}
                onClick={(event) => {
                  event.preventDefault()
                  goTo(spec.id)
                }}
              >
                <Glyph aria-hidden width={16} height={16} />
                <span>{t(spec.navKey, spec.nav)}</span>
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
